import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react"
// CORRECT IMPORTS: Use the named exports from apiClient.js
import {
  logout as apiLogout,
  login as apiLogin,
  get as apiGet,
} from "./api/apiClient"

// Define the context structure
const AuthContext = createContext(null)

/**
 * Custom hook to use the authentication context easily.
 */
export const useAuth = () => {
  return useContext(AuthContext)
}

/**
 * AuthProvider component that manages the global authentication state.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null) // Renamed from 'user'
  const [isLoading, setIsLoading] = useState(true)

  // --- Logout Function (Needs to be defined outside of useCallback usage) ---
  const handleLogout = useCallback(() => {
    apiLogout()
    setIsAuthenticated(false)
    setCurrentUser(null)
  }, [])

  // --- Profile Fetching Helper (Wrapped in useCallback) ---
  // useCallback is necessary because we use this function inside useEffect.
  const fetchUserProfile = useCallback(async () => {
    try {
      // Calls the secure API endpoint to get the client's full profile details
      const profile = await apiGet("Account/profile")

      // Update the global user state with the real data
      setCurrentUser(profile)
      setIsAuthenticated(true)
      return true
    } catch (error) {
      console.error("Failed to fetch user profile:", error)
      // If the profile fetch fails (e.g., 401 Unauthorized), we clear the session
      handleLogout()
      return false
    }
  }, [handleLogout]) // Include handleLogout in dependencies since it's used inside

  // --- Initial Load Effect ---
  // Dependency array now includes fetchUserProfile, resolving the warning
  useEffect(() => {
    const token = localStorage.getItem("jwt_token")

    if (token) {
      // If token exists, try to fetch the user profile immediately
      // This is the correct fix for page refresh authentication
      fetchUserProfile()
    }
    setIsLoading(false)
  }, [fetchUserProfile]) // Dependency added to satisfy the React warning

  // --- Login Function ---
  const handleLogin = async (email, password) => {
    setIsLoading(true)
    try {
      // 1. Call the API function to validate credentials and get the JWT
      const response = await apiLogin({ email, password })

      if (response.token) {
        // 2. Token received! Now fetch the user's full profile data
        const profileFetched = await fetchUserProfile()

        if (profileFetched) {
          setIsLoading(false)
          return response
        }
      }

      // If profile fetch fails, treat as login failure
      throw new Error("Login failed or profile could not be loaded.")
    } catch (error) {
      setIsAuthenticated(false)
      setCurrentUser(null)
      setIsLoading(false)
      throw error // Re-throw the error for the component to display
    }
  }

  const contextValue = {
    isAuthenticated,
    currentUser, // Use currentUser throughout
    isLoading,
    login: handleLogin,
    logout: handleLogout,
    updateProfileData: fetchUserProfile,
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">Loading application...</p>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

// Export the AuthProvider for use in index.js
export default AuthProvider
